import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import {
  CommunicationMessage,
  Illustrations,
  IllustrationSize,
} from '../../../Communication';
import { ResponsiveDialog } from '../../../ResponsiveDialog/ResponsiveDialog';
import { HomeworkListItem, HomeworkListItemProps } from './HomeworkListItem';
import './HomeworkListingDialog.scss';

export interface HomeworkListingDialogProps {
  open: boolean;
  onCloseClick: () => void;
  homework: HomeworkListItemProps[];
}

const HomeworkListEmptyMessage: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center emptyMsg">
      <CommunicationMessage
        illustration={Illustrations.Empty}
        illustrationSize={IllustrationSize.Small}
        headline={t('classbook:assignmentslisting:empty', { defaultValue: 'Keine Einträge vorhanden' })}
        text=""
        useDarkIllustration={false}
      />
    </div>
  );
};

export const HomeworkListingDialog: React.FC<
  HomeworkListingDialogProps
> = props => {
  const { t } = useTranslation();

  const controlledHomework = props.homework
    .filter(hw => hw.isDone)
    .map(hw => <HomeworkListItem {...hw} />);

  const uncontrolledHomework = props.homework
    .filter(hw => !hw.isDone)
    .map(hw => <HomeworkListItem {...hw} />);

  return (
    <ResponsiveDialog
      open={props.open}
      className="homeworkListingDialog"
      header={t('classbook:assignmentslisting:dialog_header', { defaultValue: 'Aufgaben' })}
      secondaryAction={
        <Button onClick={props.onCloseClick}>
          {t('close', { defaultValue: 'Schließen' })}
        </Button>
      }
    >
      <div className="flex flex-col items-stretch homeworkListWrapper">
        <div className="divider">
          {t('classbook:assignmentslisting:dialog_notchecked', { defaultValue: 'Noch nicht kontrolliert' })}
        </div>
        {uncontrolledHomework.length === 0 && <HomeworkListEmptyMessage />}
        {uncontrolledHomework.length > 0 && (
          <ul className="homeworkList" tabIndex={0}>
            {uncontrolledHomework}
          </ul>
        )}
        <div className="divider">
          {t('classbook:assignmentslisting:dialog_checked', { defaultValue: 'Bereits kontrolliert' })}
        </div>
        {controlledHomework.length === 0 && <HomeworkListEmptyMessage />}
        {controlledHomework.length > 0 && (
          <ul className="homeworkList">{controlledHomework}</ul>
        )}
      </div>
    </ResponsiveDialog>
  );
};
